import { QueryClient } from "@tanstack/react-query"

import { readsChanged, setChangeHandler } from "@/lib/api/changes"
import { isApiError } from "@/lib/api/errors"

/**
 * Defaults, not per-query settings. A query that needs different behaviour says
 * so in its own `queryOptions` in `src/queries`.
 */
export function createQueryClient(): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        /**
         * Retrying a 404 or a 422 just repeats a definitive answer, so only
         * server faults and network errors get a second and third go.
         */
        retry: (failureCount, error) => {
          if (isApiError(error) && error.isClientFault) {
            return false
          }

          return failureCount < 2
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      },
      mutations: {
        retry: false,
      },
    },
  })

  /**
   * A write's answer says which API paths it changed. What is on screen
   * refetches before the write settles; what the cache holds for screens not
   * showing refetches behind it, so the next page never opens on what was
   * just changed. Nothing here knows what any path means.
   */
  setChangeHandler(async (paths) => {
    const predicate = ({ queryKey }: { queryKey: readonly unknown[] }) =>
      readsChanged(queryKey, paths)

    await queryClient.invalidateQueries({ predicate })
    void queryClient.refetchQueries({ predicate, type: "inactive" })
  })

  return queryClient
}
